import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { SortableTableHeader } from '../components/ui/sortable-table-header';
import { Plus, MagnifyingGlass, PencilSimple, Trash, UserCheck, CaretLeft, CaretRight, ShieldCheck, Key, Cube, Calendar, SpinnerGap, Warning, UserMinus, Wrench, CheckCircle, FloppyDisk, ClockCounterClockwise, Lightning, ClipboardText, ArrowsLeftRight, PaperPlaneTilt, Eye, Power } from '@phosphor-icons/react';
import { toast } from 'sonner';
import InfoTooltip from '../components/InfoTooltip';
import EmptyState from '../components/EmptyState';

const defaultForm = { display_name: '', email: '', job_title: '', department: '', manager_id: '', manager_name: '', lifecycle_state: 'active' };
const PAGE_SIZE = 20;

const TIMELINE_CONFIG = {
  audit: { icon: PencilSimple, color: 'text-navy-600', bg: 'bg-slate-100' },
  role_assignment: { icon: ShieldCheck, color: 'text-blue-600', bg: 'bg-blue-100' },
  access_request: { icon: PaperPlaneTilt, color: 'text-orange-600', bg: 'bg-orange-100' },
  jit_session: { icon: Lightning, color: 'text-emerald-600', bg: 'bg-emerald-100' },
  certification: { icon: ClipboardText, color: 'text-teal-600', bg: 'bg-teal-100' },
  lifecycle: { icon: ArrowsLeftRight, color: 'text-purple-600', bg: 'bg-purple-100' },
};

export default function IdentitiesPage() {
  const navigate = useNavigate();
  const [identities, setIdentities] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [editingRiskScore, setEditingRiskScore] = useState(null);
  const [form, setForm] = useState(defaultForm);
  const [editing, setEditing] = useState(null);
  const [allIdentities, setAllIdentities] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [accessData, setAccessData] = useState(null);
  const [loadingAccess, setLoadingAccess] = useState(false);
  const [activeTab, setActiveTab] = useState('details');
  const [sortBy, setSortBy] = useState('display_name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [identityTab, setIdentityTab] = useState('active'); // 'active' or 'orphaned'
  const [counts, setCounts] = useState({ total: 0, active: 0, orphaned: 0 });
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  // Bulk fix wizard state
  const [showBulkFix, setShowBulkFix] = useState(false);
  const [bulkFixStep, setBulkFixStep] = useState(1); // 1=review, 2=confirm
  const [orphanedList, setOrphanedList] = useState([]);
  const [bulkEdits, setBulkEdits] = useState({}); // { identity_id: { email, department, manager_id, manager_name } }
  const [bulkSaving, setBulkSaving] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [allManagers, setAllManagers] = useState([]);
  const [timelineData, setTimelineData] = useState(null);
  const [loadingTimeline, setLoadingTimeline] = useState(false);

  const loadCounts = async () => {
    try {
      const res = await API.get('/identities/counts');
      setCounts(res.data);
    } catch (err) {
      void 0;
    }
  };

  const load = useCallback(async () => {
    try {
      const skip = (currentPage - 1) * PAGE_SIZE;
      const res = await API.get('/identities', { 
        params: { 
          search: search || undefined, 
          limit: PAGE_SIZE,
          skip: skip,
          sort_by: sortBy,
          sort_order: sortOrder,
          orphaned: identityTab === 'orphaned' ? true : false,
          department: departmentFilter || undefined,
          status: statusFilter || undefined
        } 
      });
      setIdentities(res.data.items);
      setTotal(res.data.total);
      setHasMore(res.data.has_more);
    } catch (err) {
      toast.error('Failed to load identities');
    }
  }, [search, currentPage, sortBy, sortOrder, identityTab, departmentFilter, statusFilter]);

  useEffect(() => { load(); loadCounts(); }, [load]);

  // Reset to page 1 when search, sort, or tab changes
  useEffect(() => {
    setCurrentPage(1);
  }, [search, sortBy, sortOrder, identityTab, departmentFilter, statusFilter]);

  useEffect(() => {
    // Load ALL identities for manager selection - managers can be selected regardless of orphan status
    // This allows fixing orphaned identities by assigning managers
    API.get('/identities', { params: { limit: 500, sort_by: 'display_name', sort_order: 'asc' } }).then(r => setAllIdentities(r.data.items)).catch(() => {});
  }, []);

  const totalPages = Math.ceil(total / PAGE_SIZE);
  const departments = useMemo(() =>
    [...new Set(allIdentities.map(i => i.department).filter(Boolean))].sort(),
    [allIdentities]
  );

  // Bulk fix wizard functions
  const openBulkFixWizard = async () => {
    try {
      const res = await API.get('/identities', { params: { orphaned: true, limit: 100, sort_by: 'display_name', sort_order: 'asc' } });
      const orphaned = res.data.items || [];
      setOrphanedList(orphaned);
      // Pre-populate edits with current values
      const edits = {};
      orphaned.forEach(identity => {
        edits[identity.id] = {
          email: identity.email || '',
          department: identity.department || '',
          manager_id: identity.manager_id || '',
          manager_name: identity.manager_name || '',
        };
      });
      setBulkEdits(edits);
      setBulkFixStep(1);
      setBulkResult(null);
      setShowBulkFix(true);
      // Load managers
      const mgrs = await API.get('/identities', { params: { limit: 500, sort_by: 'display_name', sort_order: 'asc' } });
      setAllManagers(mgrs.data.items || []);
    } catch {
      toast.error('Failed to load orphaned identities');
    }
  };

  const updateBulkEdit = (id, field, value) => {
    setBulkEdits(prev => ({
      ...prev,
      [id]: { ...prev[id], [field]: value }
    }));
  };

  const getMissingFields = (identity) => {
    const missing = [];
    if (!identity.email) missing.push('email');
    if (!identity.department) missing.push('department');
    if (!identity.manager_id) missing.push('manager');
    return missing;
  };

  const getEditedCount = () => {
    let count = 0;
    orphanedList.forEach(identity => {
      const edit = bulkEdits[identity.id];
      if (!edit) return;
      const changed = 
        (edit.email && edit.email !== (identity.email || '')) ||
        (edit.department && edit.department !== (identity.department || '')) ||
        (edit.manager_id && edit.manager_id !== (identity.manager_id || ''));
      if (changed) count++;
    });
    return count;
  };

  const handleBulkSave = async () => {
    const items = [];
    orphanedList.forEach(identity => {
      const edit = bulkEdits[identity.id];
      if (!edit) return;
      const changed = 
        (edit.email && edit.email !== (identity.email || '')) ||
        (edit.department && edit.department !== (identity.department || '')) ||
        (edit.manager_id && edit.manager_id !== (identity.manager_id || ''));
      if (changed) {
        items.push({
          identity_id: identity.id,
          email: edit.email || null,
          department: edit.department || null,
          manager_id: edit.manager_id || null,
          manager_name: edit.manager_name || null,
        });
      }
    });

    if (items.length === 0) {
      toast.error('No changes to save');
      return;
    }

    setBulkSaving(true);
    try {
      const res = await API.post('/identities/bulk-fix', { items });
      setBulkResult(res.data);
      setBulkFixStep(2);
      toast.success(`Updated ${res.data.updated} identities`);
      load();
      loadCounts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk save failed');
    } finally {
      setBulkSaving(false);
    }
  };

  const handleSort = (column, order) => {
    if (typeof order === 'undefined') {
      // Toggle for custom sort headers (Risk)
      order = sortBy === column && sortOrder === 'asc' ? 'desc' : 'asc';
    }
    setSortBy(column);
    setSortOrder(order);
  };

  // Client-side sort for risk_score (computed field not in DB)
  const sortedIdentities = sortBy === 'risk_score'
    ? [...identities].sort((a, b) => {
        const diff = (a.risk_score || 0) - (b.risk_score || 0);
        return sortOrder === 'asc' ? diff : -diff;
      })
    : identities;

  const handleSave = async () => {
    try {
      if (editing) {
        await API.put(`/identities/${editing}`, form);
        toast.success('Identity updated');
      } else {
        await API.post('/identities', form);
        toast.success('Identity created');
      }
      setShowDialog(false);
      setForm(defaultForm);
      setEditing(null);
      load();
      // Refresh all identities for manager dropdown
      API.get('/identities', { params: { limit: 200, sort_by: 'display_name', sort_order: 'asc' } }).then(r => setAllIdentities(r.data.items)).catch(() => {});
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    }
  };

  const handleEdit = (identity) => {
    setForm({
      display_name: identity.display_name,
      email: identity.email || '',
      job_title: identity.job_title || '',
      department: identity.department || '',
      manager_id: identity.manager_id || '',
      manager_name: identity.manager_name || '',
      lifecycle_state: identity.lifecycle_state || 'active'
    });
    setEditing(identity.id);
    setEditingRiskScore(identity.risk_score ?? null);
    setActiveTab('details');
    setAccessData(null);
    setShowDialog(true);
    
    // Load access data + timeline
    loadAccessData(identity.id);
    loadTimeline(identity.id);
  };

  const loadTimeline = async (identityId) => {
    setLoadingTimeline(true);
    try {
      const res = await API.get(`/identities/${identityId}/timeline`);
      setTimelineData(res.data);
    } catch {
      setTimelineData(null);
    } finally {
      setLoadingTimeline(false);
    }
  };

  const loadAccessData = async (identityId) => {
    setLoadingAccess(true);
    try {
      const res = await API.get(`/identities/${identityId}/access`);
      setAccessData(res.data);
    } catch (err) {
      void 0;
    } finally {
      setLoadingAccess(false);
    }
  };

  const handleRevokeRole = async (assignmentId) => {
    if (!window.confirm('Remove this role from the identity?')) return;
    try {
      await API.put(`/role-assignments/${assignmentId}/revoke`);
      toast.success('Role removed from identity');
      if (editing) loadAccessData(editing);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove role');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this identity?')) return;
    try {
      await API.delete(`/identities/${id}`);
      toast.success('Identity deleted');
      load();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const handleToggleStatus = async (identity) => {
    const action = identity.status === 'active' ? 'deactivate' : 'activate';
    if (!window.confirm(`${action.charAt(0).toUpperCase() + action.slice(1)} ${identity.display_name}?`)) return;
    try {
      await API.patch(`/identities/${identity.id}/status`);
      toast.success(`${identity.display_name} ${action}d`);
      load();
    } catch (err) {
      toast.error(`Failed to ${action}`);
    }
  };


  const statusColor = (s) => {
    if (s === 'active') return 'bg-emerald-100 text-emerald-700';
    if (s === 'inactive') return 'bg-red-100 text-red-700';
    return 'bg-yellow-100 text-yellow-700';
  };

  const lifecycleColor = (s) => {
    if (s === 'joiner') return 'bg-blue-100 text-blue-700';
    if (s === 'mover') return 'bg-amber-100 text-amber-700';
    if (s === 'leaver') return 'bg-red-100 text-red-700';
    return 'bg-slate-100 text-navy-600';
  };

  return (
    <div data-testid="identities-page" className="p-4 sm:p-6 md:p-8 space-y-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-black text-navy-900 tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Identities
          </h1>
          <p className="text-sm text-navy-500 mt-1">{counts.total} identities managed</p>
        </div>
        <Button
          onClick={() => { setForm(defaultForm); setEditing(null); setShowDialog(true); }}
          className="bg-orange-500 hover:bg-orange-600 text-white rounded-sm shadow-sm"
          data-testid="create-identity-button"
        >
          <Plus size={16} className="mr-1.5" /> New Identity
        </Button>
      </div>

      {/* Active / Orphaned Tabs */}
      <div className="flex items-center gap-4">
        <div className="flex bg-slate-100 rounded-sm p-1">
          <button
            onClick={() => setIdentityTab('active')}
            className={`px-4 py-2 text-sm font-medium rounded-sm transition-colors flex items-center gap-2 ${
              identityTab === 'active' 
                ? 'bg-white text-navy-900 shadow-sm' 
                : 'text-navy-500 hover:text-navy-700'
            }`}
            data-testid="active-identities-tab"
          >
            <UserCheck size={16} />
            Active
            <Badge className="bg-emerald-100 text-emerald-700 text-[10px] ml-1">{counts.active}</Badge>
          </button>
          <button
            onClick={() => setIdentityTab('orphaned')}
            className={`px-4 py-2 text-sm font-medium rounded-sm transition-colors flex items-center gap-2 ${
              identityTab === 'orphaned' 
                ? 'bg-white text-navy-900 shadow-sm' 
                : 'text-navy-500 hover:text-navy-700'
            }`}
            data-testid="orphaned-identities-tab"
          >
            <UserMinus size={16} />
            Orphaned <InfoTooltip concept="orphaned" />
            <Badge className="bg-amber-100 text-amber-700 text-[10px] ml-1">{counts.orphaned}</Badge>
          </button>
        </div>
        
        {identityTab === 'orphaned' && (
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 text-xs text-amber-600 bg-amber-50 px-3 py-1.5 rounded-sm">
              <Warning size={14} />
              <span>Orphaned identities are missing email, department, or manager and cannot be used for role assignments</span>
            </div>
            {counts.orphaned > 0 && (
              <Button
                data-testid="bulk-fix-wizard-btn"
                onClick={openBulkFixWizard}
                className="bg-amber-500 hover:bg-amber-600 text-white shrink-0"
                size="sm"
              >
                <Wrench size={14} className="mr-1" /> Bulk Fix Wizard
              </Button>
            )}
          </div>
        )}
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 max-w-sm">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-navy-400" />
          <Input
            placeholder="Search identities..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 h-9 rounded-sm border-slate-200"
            data-testid="identity-search-input"
          />
        </div>
        <Select value={departmentFilter || 'all_departments'} onValueChange={(v) => setDepartmentFilter(v === 'all_departments' ? '' : v)}>
          <SelectTrigger className="w-44 h-9 rounded-sm text-xs" data-testid="identity-dept-filter">
            <SelectValue placeholder="All Departments" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all_departments">All Departments</SelectItem>
            {[...new Set(allIdentities.map(i => i.department).filter(Boolean))].sort().map(d => (
              <SelectItem key={d} value={d}>{d}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={statusFilter || 'all_statuses'} onValueChange={(v) => setStatusFilter(v === 'all_statuses' ? '' : v)}>
          <SelectTrigger className="w-32 h-9 rounded-sm text-xs" data-testid="identity-status-filter">
            <SelectValue placeholder="All Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all_statuses">All Status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
          </SelectContent>
        </Select>
        {(departmentFilter || statusFilter) && (
          <Button variant="ghost" size="sm" onClick={() => { setDepartmentFilter(''); setStatusFilter(''); }} className="text-xs text-navy-500 h-9">
            Clear filters
          </Button>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-sm shadow-sm overflow-hidden">
        <div className="h-1.5 bg-navy-600" />
        <Table wrapperClassName="max-h-[calc(100vh-340px)]">
          <TableHeader>
            <TableRow className="bg-slate-50 shadow-[0_1px_3px_rgba(0,0,0,0.05)]">
              <SortableTableHeader column="display_name" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort}>Name</SortableTableHeader>
              <SortableTableHeader column="email" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort}>Email</SortableTableHeader>
              <SortableTableHeader column="department" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort}>Department</SortableTableHeader>
              <SortableTableHeader column="manager_name" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort}>Manager</SortableTableHeader>
              <SortableTableHeader column="status" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort}>Status</SortableTableHeader>
              <TableHead className="text-[10px] font-bold uppercase tracking-widest text-navy-500 cursor-pointer select-none" onClick={() => handleSort('risk_score')} data-testid="sort-risk">
                <div className="flex items-center gap-1">
                  Risk
                  {sortBy === 'risk_score' && <span className="text-orange-500">{sortOrder === 'asc' ? '↑' : '↓'}</span>}
                </div>
              </TableHead>
              <SortableTableHeader column="lifecycle_state" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort}>Lifecycle</SortableTableHeader>
              <TableHead className="text-[10px] font-bold uppercase tracking-widest text-navy-500 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {identities.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8}>
                  {identityTab === 'orphaned'
                    ? <div className="text-center py-12 text-sm text-navy-400">No orphaned identities found. All identities have required fields.</div>
                    : <EmptyState type="identities" onAction={() => { setForm(defaultForm); setEditing(null); setShowDialog(true); }} />
                  }
                </TableCell>
              </TableRow>
            ) : sortedIdentities.map((item) => (
              <TableRow key={item.id} className="hover:bg-slate-50 transition-colors cursor-pointer" data-testid={`identity-row-${item.id}`} onClick={() => handleEdit(item)}>
                <TableCell className="font-medium text-sm text-navy-900">
                  <div className="flex items-center gap-2">
                    {item.display_name}
                    {item.is_orphaned && (
                      <span className="text-amber-500" title="Missing required fields">
                        <Warning size={14} />
                      </span>
                    )}
                  </div>
                </TableCell>
                <TableCell className={`text-sm ${!item.email ? 'text-red-400 italic' : 'text-navy-600'}`}>
                  {item.email || 'Missing'}
                </TableCell>
                <TableCell className={`text-sm ${!item.department ? 'text-red-400 italic' : 'text-navy-600'}`}>
                  {item.department || 'Missing'}
                </TableCell>
                <TableCell className={`text-sm ${!item.manager_id ? 'text-red-400 italic' : 'text-navy-600'}`}>
                  {item.manager_name ? (
                    <span className="flex items-center gap-1">
                      <UserCheck size={12} className="text-navy-400" /> {item.manager_name}
                    </span>
                  ) : 'Missing'}
                </TableCell>
                <TableCell>
                  <span className={`inline-block px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase ${statusColor(item.status)}`}>
                    {item.status}
                  </span>
                </TableCell>
                <TableCell>
                  {(() => {
                    const rs = item.risk_score || 0;
                    const color = rs >= 75 ? 'text-red-600 bg-red-50' : rs >= 50 ? 'text-amber-600 bg-amber-50' : rs >= 25 ? 'text-yellow-600 bg-yellow-50' : 'text-emerald-600 bg-emerald-50';
                    return (
                      <span data-testid={`risk-score-${item.id}`} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[10px] font-bold ${color}`}>
                        {rs}
                      </span>
                    );
                  })()}
                </TableCell>
                <TableCell>
                  <span className={`inline-block px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase ${lifecycleColor(item.lifecycle_state)}`}>
                    {item.lifecycle_state}
                  </span>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                    <Button variant="ghost" size="sm" onClick={() => navigate(`/identities/${item.id}`)} data-testid={`view-identity-${item.id}`} title="View 360">
                      <Eye size={14} />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleEdit(item)} data-testid={`edit-identity-${item.id}`}>
                      <PencilSimple size={14} />
                    </Button>
                    <Button variant="ghost" size="sm"
                      onClick={() => handleToggleStatus(item)}
                      className={item.status === 'active' ? 'text-amber-600 hover:text-amber-800' : 'text-emerald-600 hover:text-emerald-800'}
                      data-testid={`toggle-status-${item.id}`}
                      title={item.status === 'active' ? 'Deactivate' : 'Activate'}
                    >
                      <Power size={14} />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(item.id)} className="text-red-500 hover:text-red-700" data-testid={`delete-identity-${item.id}`}>
                      <Trash size={14} />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Pagination Controls */}
      {total > 0 && (
        <div className="flex items-center justify-between bg-white border border-slate-200 rounded-sm shadow-sm px-4 py-3">
          <div className="text-sm text-navy-500">
            Showing <span className="font-medium text-navy-700">{((currentPage - 1) * PAGE_SIZE) + 1}</span> to{' '}
            <span className="font-medium text-navy-700">{Math.min(currentPage * PAGE_SIZE, total)}</span> of{' '}
            <span className="font-medium text-navy-700">{total}</span> identities
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="rounded-sm h-8 px-3"
              data-testid="prev-page-button"
            >
              <CaretLeft size={14} className="mr-1" /> Previous
            </Button>
            <div className="flex items-center gap-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                return (
                  <Button
                    key={pageNum}
                    variant={currentPage === pageNum ? "default" : "outline"}
                    size="sm"
                    onClick={() => setCurrentPage(pageNum)}
                    className={`rounded-sm h-8 w-8 p-0 ${currentPage === pageNum ? 'bg-orange-500 hover:bg-orange-600 text-white' : ''}`}
                    data-testid={`page-${pageNum}-button`}
                  >
                    {pageNum}
                  </Button>
                );
              })}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages || !hasMore}
              className="rounded-sm h-8 px-3"
              data-testid="next-page-button"
            >
              Next <CaretRight size={14} className="ml-1" />
            </Button>
          </div>
        </div>
      )}

      <Dialog open={showDialog} onOpenChange={(open) => { setShowDialog(open); if (!open) { setAccessData(null); setActiveTab('details'); } }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3" style={{ fontFamily: 'Chivo, sans-serif' }}>
              <span>{editing ? 'View / Edit Identity' : 'Create Identity'}</span>
              {editing && editingRiskScore != null && (() => {
                const rs = editingRiskScore;
                const riskColor = rs >= 75 ? 'bg-red-100 text-red-700' : rs >= 50 ? 'bg-amber-100 text-amber-700' : rs >= 25 ? 'bg-yellow-100 text-yellow-700' : 'bg-emerald-100 text-emerald-700';
                const riskLabel = rs >= 75 ? 'Critical' : rs >= 50 ? 'High' : rs >= 25 ? 'Medium' : 'Low';
                return (
                  <Badge data-testid="dialog-risk-score" className={`text-[10px] px-2 py-0.5 ${riskColor}`}>
                    Risk: {rs} ({riskLabel})
                  </Badge>
                );
              })()}
            </DialogTitle>
          </DialogHeader>
          
          {editing ? (
            <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
              <TabsList className="bg-slate-100 rounded-sm p-0.5 shrink-0">
                <TabsTrigger value="details" className="rounded-sm text-xs data-[state=active]:bg-white data-[state=active]:shadow-sm px-4 py-2">
                  <PencilSimple size={14} className="mr-1.5" /> Details
                </TabsTrigger>
                <TabsTrigger value="roles" className="rounded-sm text-xs data-[state=active]:bg-white data-[state=active]:shadow-sm px-4 py-2">
                  <ShieldCheck size={14} className="mr-1.5" /> Roles
                  {accessData?.summary?.total_roles > 0 && (
                    <Badge className="ml-1.5 bg-blue-100 text-blue-700 text-[10px]">{accessData.summary.total_roles}</Badge>
                  )}
                </TabsTrigger>
                <TabsTrigger value="entitlements" className="rounded-sm text-xs data-[state=active]:bg-white data-[state=active]:shadow-sm px-4 py-2">
                  <Key size={14} className="mr-1.5" /> Entitlements
                  {accessData?.summary?.total_entitlements > 0 && (
                    <Badge className="ml-1.5 bg-amber-100 text-amber-700 text-[10px]">{accessData.summary.total_entitlements}</Badge>
                  )}
                </TabsTrigger>
                <TabsTrigger value="applications" className="rounded-sm text-xs data-[state=active]:bg-white data-[state=active]:shadow-sm px-4 py-2">
                  <Cube size={14} className="mr-1.5" /> Applications
                  {accessData?.summary?.total_applications > 0 && (
                    <Badge className="ml-1.5 bg-emerald-100 text-emerald-700 text-[10px]">{accessData.summary.total_applications}</Badge>
                  )}
                </TabsTrigger>
                <TabsTrigger value="activity" className="rounded-sm text-xs data-[state=active]:bg-white data-[state=active]:shadow-sm px-4 py-2" data-testid="identity-activity-tab">
                  <ClockCounterClockwise size={14} className="mr-1.5" /> Activity
                  {timelineData?.total > 0 && (
                    <Badge className="ml-1.5 bg-orange-100 text-orange-700 text-[10px]">{timelineData.total}</Badge>
                  )}
                </TabsTrigger>
              </TabsList>

              <TabsContent value="details" className="flex-1 overflow-auto mt-4">
                <IdentityDetailsForm form={form} setForm={setForm} allIdentities={allIdentities} editing={editing} />
              </TabsContent>

              <TabsContent value="roles" className="flex-1 overflow-auto mt-4">
                {loadingAccess ? (
                  <div className="flex items-center justify-center py-12">
                    <SpinnerGap className="h-6 w-6 animate-spin text-orange-500" />
                    <span className="ml-2 text-sm text-navy-500">Loading roles...</span>
                  </div>
                ) : accessData?.roles?.length > 0 ? (
                  <div className="space-y-3">
                    {accessData.roles.map((role) => (
                      <div key={role.assignment_id} className="bg-slate-50 border border-slate-200 rounded-sm p-4">
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2">
                              <ShieldCheck size={16} className="text-blue-600" />
                              <span className="font-medium text-sm text-navy-900">{role.role_name}</span>
                              <Badge className={`text-[10px] ${role.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-navy-600'}`}>
                                {role.status}
                              </Badge>
                              <Badge className={`text-[10px] ${role.assignment_type === 'automatic' ? 'bg-purple-100 text-purple-700' : 'bg-slate-100 text-navy-600'}`}>
                                {role.assignment_type}
                              </Badge>
                            </div>
                            {role.role_description && (
                              <p className="text-xs text-navy-500 mt-1 ml-6">{role.role_description}</p>
                            )}
                            {role.entitlement_ids?.length > 0 && (
                              <p className="text-xs text-navy-400 mt-1 ml-6">
                                Includes {role.entitlement_ids.length} entitlement{role.entitlement_ids.length !== 1 ? 's' : ''}
                              </p>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            {role.expires_at && (
                              <div className="text-right">
                                <div className="flex items-center gap-1 text-xs text-amber-600">
                                  <Calendar size={12} />
                                  <span>Expires: {new Date(role.expires_at).toLocaleDateString()}</span>
                                </div>
                              </div>
                            )}
                            {role.status === 'active' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRevokeRole(role.assignment_id)}
                                className="text-red-500 hover:text-red-700 hover:bg-red-50 h-7 px-2"
                                data-testid={`revoke-role-${role.assignment_id}`}
                              >
                                <Trash size={14} className="mr-1" />
                                <span className="text-xs">Remove</span>
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-sm text-navy-400">
                    <ShieldCheck size={32} className="mx-auto mb-2 text-navy-300" />
                    No roles assigned to this identity
                  </div>
                )}
              </TabsContent>

              <TabsContent value="entitlements" className="flex-1 overflow-auto mt-4">
                {loadingAccess ? (
                  <div className="flex items-center justify-center py-12">
                    <SpinnerGap className="h-6 w-6 animate-spin text-orange-500" />
                    <span className="ml-2 text-sm text-navy-500">Loading entitlements...</span>
                  </div>
                ) : accessData?.entitlements?.length > 0 ? (
                  <div className="space-y-4">
                    {/* Summary stats */}
                    {accessData.summary && (
                      <div className="grid grid-cols-3 gap-3">
                        <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 text-center">
                          <p className="text-xl font-bold text-amber-700">{accessData.summary.total_entitlements}</p>
                          <p className="text-[10px] text-amber-600 uppercase font-semibold">Total Entitlements</p>
                        </div>
                        <div className="bg-purple-50 border border-purple-200 rounded-sm p-3 text-center">
                          <p className="text-xl font-bold text-purple-700">{accessData.summary.entitlements_via_roles || 0}</p>
                          <p className="text-[10px] text-purple-600 uppercase font-semibold">Via Roles</p>
                        </div>
                        <div className="bg-blue-50 border border-blue-200 rounded-sm p-3 text-center">
                          <p className="text-xl font-bold text-blue-700">{accessData.summary.direct_entitlements_from_azure || 0}</p>
                          <p className="text-[10px] text-blue-600 uppercase font-semibold">Direct Azure AD</p>
                        </div>
                      </div>
                    )}
                    
                    <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-slate-50">
                            <TableHead className="text-[10px] font-bold uppercase tracking-widest text-navy-500">Entitlement (Group)</TableHead>
                            <TableHead className="text-[10px] font-bold uppercase tracking-widest text-navy-500">Type</TableHead>
                            <TableHead className="text-[10px] font-bold uppercase tracking-widest text-navy-500">Access Source</TableHead>
                            <TableHead className="text-[10px] font-bold uppercase tracking-widest text-navy-500">Origin</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {accessData.entitlements.map((ent) => (
                            <TableRow key={ent.id} className="hover:bg-slate-50">
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  <Key size={14} className={ent.granted_via === 'direct_azure_ad' ? 'text-blue-600' : 'text-amber-600'} />
                                  <div>
                                    <span className="font-medium text-sm text-navy-900">{ent.name}</span>
                                    {ent.description && (
                                      <p className="text-xs text-navy-500">{ent.description}</p>
                                    )}
                                  </div>
                                </div>
                              </TableCell>
                              <TableCell>
                                <Badge className="text-[10px] bg-slate-100 text-navy-600">{ent.type || 'security'}</Badge>
                              </TableCell>
                              <TableCell>
                                <div className="flex flex-wrap gap-1">
                                  {ent.granted_via === 'direct_azure_ad' ? (
                                    <Badge className="bg-blue-100 text-blue-700 text-[10px]">
                                      <Key size={10} className="mr-1" />Direct Azure AD
                                    </Badge>
                                  ) : (ent.granted_via_roles || []).length > 0 ? (
                                    (ent.granted_via_roles || []).map((role, idx) => (
                                      <Badge key={role.role_id || role.role_name} className="bg-purple-100 text-purple-700 text-[10px]">
                                        <ShieldCheck size={10} className="mr-1" />{role.role_name}
                                      </Badge>
                                    ))
                                  ) : (
                                    <span className="text-xs text-navy-400">Unknown</span>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell>
                                <span className="text-xs text-navy-500">{ent.source || 'manual'}</span>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12 text-sm text-navy-400">
                    <Key size={32} className="mx-auto mb-2 text-navy-300" />
                    No entitlements assigned to this identity
                  </div>
                )}
              </TabsContent>

              <TabsContent value="applications" className="flex-1 overflow-auto mt-4">
                {loadingAccess ? (
                  <div className="flex items-center justify-center py-12">
                    <SpinnerGap className="h-6 w-6 animate-spin text-orange-500" />
                    <span className="ml-2 text-sm text-navy-500">Loading applications...</span>
                  </div>
                ) : accessData?.applications?.length > 0 ? (
                  <div className="grid grid-cols-2 gap-3">
                    {accessData.applications.map((app) => (
                      <div key={app.id} className="bg-slate-50 border border-slate-200 rounded-sm p-4">
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 rounded-sm bg-orange-100 flex items-center justify-center shrink-0">
                            <Cube size={20} className="text-orange-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-sm text-navy-900 truncate">{app.name}</span>
                              <Badge className={`text-[10px] shrink-0 ${app.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-navy-600'}`}>
                                {app.status}
                              </Badge>
                            </div>
                            {app.description && (
                              <p className="text-xs text-navy-500 mt-1 truncate">{app.description}</p>
                            )}
                            {app.owner && (
                              <p className="text-xs text-navy-400 mt-1">Owner: {app.owner}</p>
                            )}
                            <div className="mt-2">
                              <p className="text-[10px] text-navy-500 uppercase font-semibold mb-1">Roles via this app:</p>
                              <div className="flex flex-wrap gap-1">
                                {(app.matching_roles || []).map((roleName, idx) => (
                                  <Badge key={roleName} className="bg-blue-100 text-blue-700 text-[10px]">
                                    <ShieldCheck size={10} className="mr-1" />{roleName}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-sm text-navy-400">
                    <Cube size={32} className="mx-auto mb-2 text-navy-300" />
                    No applications accessible by this identity
                  </div>
                )}
              </TabsContent>

              {/* Activity Timeline Tab */}
              <TabsContent value="activity" className="flex-1 overflow-auto mt-4" data-testid="identity-activity-content">
                {loadingTimeline ? (
                  <div className="flex items-center justify-center py-12">
                    <SpinnerGap className="h-6 w-6 animate-spin text-orange-500" />
                    <span className="ml-2 text-sm text-navy-500">Loading timeline...</span>
                  </div>
                ) : timelineData?.events?.length > 0 ? (
                  <div className="relative pl-6">
                    {/* Vertical line */}
                    <div className="absolute left-[11px] top-2 bottom-2 w-px bg-slate-200" />
                    
                    <div className="space-y-0">
                      {timelineData.events.map((event, idx) => {
                        const cfg = TIMELINE_CONFIG[event.type] || TIMELINE_CONFIG.audit;
                        const Icon = cfg.icon;
                        return (
                          <div key={event.id || idx} className="relative flex gap-3 pb-5" data-testid={`timeline-event-${event.id || idx}`}>
                            {/* Dot */}
                            <div className={`absolute -left-6 w-[22px] h-[22px] rounded-full border-2 border-white shadow-sm flex items-center justify-center shrink-0 ${cfg.bg}`}>
                              <Icon size={10} weight="bold" className={cfg.color} />
                            </div>
                            
                            {/* Content */}
                            <div className="flex-1 min-w-0 pt-0.5">
                              <div className="flex items-start justify-between gap-2">
                                <div className="min-w-0">
                                  <p className="text-sm font-medium text-navy-900 truncate">{event.title}</p>
                                  {event.detail && (
                                    <p className="text-xs text-navy-500 mt-0.5 truncate">{event.detail}</p>
                                  )}
                                </div>
                                <div className="text-right shrink-0">
                                  <p className="text-[10px] text-navy-400">{event.timestamp ? new Date(event.timestamp).toLocaleDateString() : ''}</p>
                                  <p className="text-[10px] text-navy-300">{event.timestamp ? new Date(event.timestamp).toLocaleTimeString() : ''}</p>
                                </div>
                              </div>
                              <p className="text-[10px] text-navy-400 mt-1">by {event.actor}</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12 text-navy-400">
                    <ClockCounterClockwise size={32} className="mx-auto mb-2 text-navy-300" />
                    No activity recorded for this identity
                  </div>
                )}
              </TabsContent>
            </Tabs>
          ) : (
            <IdentityDetailsForm form={form} setForm={setForm} allIdentities={allIdentities} editing={editing} />
          )}
          
          <DialogFooter className="shrink-0 mt-4 pt-4 border-t border-slate-200">
            <Button variant="outline" onClick={() => setShowDialog(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={handleSave} className="bg-orange-500 hover:bg-orange-600 text-white rounded-sm" data-testid="save-identity-button">
              {editing ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Fix Wizard Dialog */}
      <Dialog open={showBulkFix} onOpenChange={setShowBulkFix}>
        <DialogContent className="max-w-5xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Chivo, sans-serif' }} className="flex items-center gap-2">
              <Wrench size={20} className="text-amber-500" />
              Bulk Fix Orphaned Identities
            </DialogTitle>
            <p className="text-sm text-navy-500 mt-1">
              {bulkFixStep === 1
                ? `${orphanedList.length} orphaned identities found. Fill in missing fields and save all at once.`
                : `Update complete.`
              }
            </p>
          </DialogHeader>

          {bulkFixStep === 1 && (
            <>
              <div className="flex-1 overflow-y-auto border border-slate-200 rounded-lg" data-testid="bulk-fix-table">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b border-slate-200 sticky top-0 z-10">
                    <tr>
                      <th className="text-left px-3 py-2.5 text-xs font-semibold text-navy-500 uppercase w-40">Name</th>
                      <th className="text-left px-3 py-2.5 text-xs font-semibold text-navy-500 uppercase">Missing</th>
                      <th className="text-left px-3 py-2.5 text-xs font-semibold text-navy-500 uppercase">Email</th>
                      <th className="text-left px-3 py-2.5 text-xs font-semibold text-navy-500 uppercase">Department</th>
                      <th className="text-left px-3 py-2.5 text-xs font-semibold text-navy-500 uppercase">Manager</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {orphanedList.length === 0 ? (
                      <tr><td colSpan={5} className="text-center py-8 text-navy-400">No orphaned identities</td></tr>
                    ) : orphanedList.map(identity => {
                      const missing = getMissingFields(identity);
                      const edit = bulkEdits[identity.id] || {};
                      return (
                        <tr key={identity.id} className="hover:bg-slate-50" data-testid={`bulk-fix-row-${identity.id}`}>
                          <td className="px-3 py-2">
                            <p className="font-medium text-navy-900 text-xs truncate max-w-[140px]">{identity.display_name}</p>
                            <p className="text-[10px] text-navy-400 truncate max-w-[140px]">{identity.source || 'manual'}</p>
                          </td>
                          <td className="px-3 py-2">
                            <div className="flex gap-1 flex-wrap">
                              {missing.map(f => (
                                <Badge key={f} className="bg-red-100 text-red-600 text-[9px] uppercase">{f}</Badge>
                              ))}
                            </div>
                          </td>
                          <td className="px-3 py-2">
                            <Input
                              value={edit.email || ''}
                              onChange={(e) => updateBulkEdit(identity.id, 'email', e.target.value)}
                              placeholder="user@example.com"
                              className={`h-8 text-xs rounded-sm ${!identity.email && !edit.email ? 'border-red-300 bg-red-50/50' : edit.email !== (identity.email || '') ? 'border-emerald-300 bg-emerald-50/50' : ''}`}
                              data-testid={`bulk-fix-email-${identity.id}`}
                            />
                          </td>
                          <td className="px-3 py-2">
                            <Input
                              value={edit.department || ''}
                              onChange={(e) => updateBulkEdit(identity.id, 'department', e.target.value)}
                              placeholder="e.g., Engineering"
                              className={`h-8 text-xs rounded-sm ${!identity.department && !edit.department ? 'border-red-300 bg-red-50/50' : edit.department !== (identity.department || '') ? 'border-emerald-300 bg-emerald-50/50' : ''}`}
                              data-testid={`bulk-fix-dept-${identity.id}`}
                            />
                          </td>
                          <td className="px-3 py-2">
                            <select
                              value={edit.manager_id || ''}
                              onChange={(e) => {
                                const mgr = allManagers.find(m => m.id === e.target.value);
                                updateBulkEdit(identity.id, 'manager_id', e.target.value);
                                updateBulkEdit(identity.id, 'manager_name', mgr?.display_name || '');
                              }}
                              className={`w-full h-8 text-xs rounded-sm border px-2 ${!identity.manager_id && !edit.manager_id ? 'border-red-300 bg-red-50/50' : edit.manager_id !== (identity.manager_id || '') ? 'border-emerald-300 bg-emerald-50/50' : 'border-slate-200'}`}
                              data-testid={`bulk-fix-mgr-${identity.id}`}
                            >
                              <option value="">Select manager...</option>
                              {allManagers
                                .filter(m => m.id !== identity.id && m.email)
                                .map(m => (
                                  <option key={m.id} value={m.id}>{m.display_name}</option>
                                ))}
                            </select>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <DialogFooter className="flex items-center justify-between sm:justify-between pt-3 border-t border-slate-200">
                <div className="text-xs text-navy-500">
                  {getEditedCount()} of {orphanedList.length} identities modified
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setShowBulkFix(false)} className="rounded-sm">Cancel</Button>
                  <Button
                    data-testid="bulk-fix-save-btn"
                    onClick={handleBulkSave}
                    disabled={getEditedCount() === 0 || bulkSaving}
                    className="bg-amber-500 hover:bg-amber-600 text-white rounded-sm"
                  >
                    {bulkSaving ? (
                      <SpinnerGap size={14} className="animate-spin mr-1" />
                    ) : (
                      <FloppyDisk size={14} className="mr-1" />
                    )}
                    Save {getEditedCount()} Changes
                  </Button>
                </div>
              </DialogFooter>
            </>
          )}

          {bulkFixStep === 2 && bulkResult && (
            <div className="py-6 text-center space-y-4" data-testid="bulk-fix-result">
              <CheckCircle size={48} weight="fill" className="text-emerald-500 mx-auto" />
              <h3 className="text-lg font-semibold text-navy-900">Bulk Fix Complete</h3>
              <div className="flex items-center justify-center gap-6">
                <div className="text-center">
                  <p className="text-2xl font-bold text-emerald-600">{bulkResult.updated}</p>
                  <p className="text-xs text-navy-500 uppercase">Updated</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-navy-400">{bulkResult.skipped}</p>
                  <p className="text-xs text-navy-500 uppercase">Skipped</p>
                </div>
                {bulkResult.errors?.length > 0 && (
                  <div className="text-center">
                    <p className="text-2xl font-bold text-red-500">{bulkResult.errors.length}</p>
                    <p className="text-xs text-navy-500 uppercase">Errors</p>
                  </div>
                )}
              </div>
              <Button onClick={() => setShowBulkFix(false)} className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-sm mt-4">
                Done
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Extracted form component for reuse
function IdentityDetailsForm({ form, setForm, allIdentities, editing }) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="text-xs font-semibold uppercase tracking-widest">Display Name *</Label>
          <Input value={form.display_name} onChange={(e) => setForm({...form, display_name: e.target.value})} className="mt-1 rounded-sm" data-testid="identity-name-input" />
        </div>
        <div>
          <Label className="text-xs font-semibold uppercase tracking-widest">Email</Label>
          <Input value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} className="mt-1 rounded-sm" data-testid="identity-email-input" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="text-xs font-semibold uppercase tracking-widest">Job Title</Label>
          <Input value={form.job_title} onChange={(e) => setForm({...form, job_title: e.target.value})} className="mt-1 rounded-sm" />
        </div>
        <div>
          <Label className="text-xs font-semibold uppercase tracking-widest">Department</Label>
          <Input value={form.department} onChange={(e) => setForm({...form, department: e.target.value})} className="mt-1 rounded-sm" />
        </div>
      </div>
      <div>
        <Label className="text-xs font-semibold uppercase tracking-widest">Manager</Label>
        <Select
          value={form.manager_id || "none"}
          onValueChange={(val) => {
            const mgr = allIdentities.find(i => i.id === val);
            setForm({...form, manager_id: val === "none" ? "" : val, manager_name: mgr ? mgr.display_name : ''});
          }}
        >
          <SelectTrigger className="mt-1 rounded-sm" data-testid="identity-manager-select">
            <SelectValue placeholder="Select a manager" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">No Manager</SelectItem>
            {allIdentities.filter(i => i.id !== editing).map(i => (
              <SelectItem key={i.id} value={i.id}>{i.display_name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-xs font-semibold uppercase tracking-widest">Lifecycle State</Label>
        <Select value={form.lifecycle_state} onValueChange={(val) => setForm({...form, lifecycle_state: val})}>
          <SelectTrigger className="mt-1 rounded-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="joiner">Joiner</SelectItem>
            <SelectItem value="mover">Mover</SelectItem>
            <SelectItem value="leaver">Leaver</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}
